/**
 * Keeps the featured testimonial and the card row in sync, and disables the arrows at either end.
 */
class VTSTestimonialsCarousel {
    static READY_CLASS = 'testimonials-carousel--ready';

    constructor(element) {
        this.element = element;
        this.cardsElement = element.querySelector(
            '.testimonials-carousel__cards'
        );
        this.featured = element.querySelector(
            '.testimonials-carousel__featured'
        )?.swiper;
        this.cards = this.cardsElement?.swiper;
        this.prev = element.querySelector(
            '.testimonials-carousel__arrow--prev'
        );
        this.next = element.querySelector(
            '.testimonials-carousel__arrow--next'
        );

        if (!this.featured || !this.cards) {
            return;
        }

        // swiper-carousel.js turns keyboard control on for every carousel; leave it to the featured one so an
        // arrow key doesn't move both and advance twice.
        this.cards.keyboard?.disable();

        this.bindEvents();
        this.updateCardFocus();
        element.classList.add(VTSTestimonialsCarousel.READY_CLASS);
        // Ensures Swiper counts slides that are initially display: none. Until then it sees a single slide and
        // reports isEnd, so update() has to run after this to set the arrows.
        this.featured.update();
        this.update();
    }

    bindEvents() {
        this.prev.addEventListener('click', () => this.featured.slidePrev());
        this.next.addEventListener('click', () => this.featured.slideNext());

        this.cardsElement.addEventListener('click', (event) => {
            const slide = event.target.closest('swiper-slide');

            if (!slide) {
                return;
            }

            this.featured.slideTo(this.cards.slides.indexOf(slide));
        });

        this.featured.on('slideChange', () => {
            this.cards.slideTo(this.featured.activeIndex);
            this.update();
        });

        this.cards.on('slideChangeTransitionEnd', () => this.updateCardFocus());
        this.cards.on('resize', () => {
            // Swiper's resize jumps a row that was at its end to the last slide, which can hide the active card.
            this.cards.slideTo(this.featured.activeIndex, 0, false);
            this.updateCardFocus();
        });
    }

    update() {
        const index = this.featured.activeIndex;
        const activeSlide = this.featured.slides[index];
        const focusedSlide = this.featured.slides.find(
            (slide) =>
                slide !== activeSlide && slide.contains(document.activeElement)
        );

        // Faded-out slides stay in the layout, so keep their videos and
        // controls out of the tab order and stop anything still playing.
        this.featured.slides.forEach((slide) => {
            slide.inert = slide !== activeSlide;

            if (slide.inert) {
                slide.querySelectorAll('wistia-player').forEach((player) => {
                    VTSWistiaPlayer.instances.get(player.id)?.pause();
                });
            }
        });

        // Keyboard navigation from the play button would otherwise drop focus to <body>.
        if (focusedSlide) {
            activeSlide.querySelector('button')?.focus({ preventScroll: true });
        }

        // aria-disabled rather than disabled, which would drop focus to <body> when the arrow reaches the end.
        this.prev.setAttribute('aria-disabled', this.featured.isBeginning);
        this.next.setAttribute('aria-disabled', this.featured.isEnd);

        this.element
            .querySelectorAll('.testimonials-carousel__card')
            .forEach((card) => {
                const isActive = Number(card.dataset.index) === index;
                card.classList.toggle('is-active', isActive);
                if (isActive) {
                    card.setAttribute('aria-current', 'true');
                } else {
                    card.removeAttribute('aria-current');
                }
            });
    }

    // Only cards in view can be focused or read by screen readers.
    updateCardFocus() {
        const focused = this.cards.slides.find((slide) =>
            slide.contains(document.activeElement)
        );

        this.cards.slides.forEach((slide) => {
            slide.inert = !slide.classList.contains('swiper-slide-visible');
        });

        // Swiper's arrow keys work without focus in the carousel, so the row can scroll the focused card out of
        // view, which would drop focus to <body>. The active card is always in view.
        if (focused?.inert) {
            this.cards.slides[this.featured.activeIndex]
                .querySelector('button')
                .focus({ preventScroll: true });
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document
        .querySelectorAll('.testimonials-carousel')
        .forEach((element) => new VTSTestimonialsCarousel(element));
});
